"use client";

import React, { useState, useMemo, useEffect, useRef } from "react";
import { createTranslator, createFormatter, type Locale } from "@/utils/i18n";
import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  addDays,
  differenceInDays,
  isBefore,
  isAfter,
  isSameMonth,
  startOfDay,
  differenceInMonths,
} from "date-fns";

import { cn } from "@/utils/classNames";
import { PlaneLanding, PlaneTakeoff, Euro, X, ShoppingCart, AlertCircle } from "lucide-react";
import { StockItem } from "@/types";
import {
  trackSelectArrivalDate,
  trackSelectDepartureDate,
  trackClearDateSelection,
  trackBeginCheckout,
  trackViewPricesClick,
  trackNavigateMonth,
  trackClickUnavailableDate,
  trackMinimumStayError,
  trackViewPrice,
} from "@/utils/tracking";

import { FixedSizeList as List, ListOnScrollProps } from "react-window";

// Simple Button component
function Button({
  children,
  onClick,
  className,
  disabled,
  id,
  href,
  target,
  rel,
  variant = "default",
  size = "default",
  style,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  id?: string;
  href?: string;
  target?: string;
  rel?: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm";
  style?: React.CSSProperties;
}) {
  const baseStyles = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
  const variantStyles = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    ghost: "", // No default background, let className control it
  };
  const sizeStyles = {
    default: "h-10 px-4 py-2",
    sm: "h-9 px-3 text-sm",
  };

  const styles = cn(baseStyles, variantStyles[variant], sizeStyles[size], className);

  if (href) {
    return (
      <a
        id={id}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={styles}
        style={style}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      id={id}
      onClick={onClick}
      disabled={disabled}
      className={styles}
      style={style}
    >
      {children}
    </button>
  );
}

// Simple Card components
function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)}>
      {children}
    </div>
  );
}

function CardHeader({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex flex-col space-y-1.5 p-6", className)}>{children}</div>;
}

function CardTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h3 className={cn("text-2xl font-semibold leading-none tracking-tight", className)}>{children}</h3>;
}

function CardContent({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("p-6 pt-0", className)}>{children}</div>;
}

function CardFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex items-center p-6 pt-0", className)}>{children}</div>;
}

function Separator({ className }: { className?: string }) {
  return <div className={cn("shrink-0 bg-border h-[1px] w-full", className)} />;
}

function useAvailabilityData(availabilityData: StockItem[]) {
  return useMemo(() => {
    const dateToStockItem = new Map<string, StockItem>(
      availabilityData.map((item) => [format(new Date(item.valid_at), "yyyy-MM-dd"), item])
    );

    function isDateAvailable(
      date: Date,
      selectedArrival: Date | null
    ): { isAvailable: boolean; reason?: string } {
      const today = startOfDay(new Date());
      const dateString = format(date, "yyyy-MM-dd");

      if (isBefore(date, today)) {
        return { isAvailable: false, reason: "pastDate" };
      }

      if (selectedArrival === null) {
        // No arrival date selected. We need to check if date is available as an arrival date.

        // Get the StockItem for the date.
        const stockItem = dateToStockItem.get(dateString);
        if (!stockItem || stockItem.allotment === 0 || stockItem.is_stopped === 1) {
          return { isAvailable: false, reason: "noAllotment" };
        }

        // Need to check if there are at least minimum_stay consecutive nights of availability starting from this date.
        const minimumStay = stockItem.maximum_minimum_stay || 1;
        for (let i = 0; i < minimumStay; i++) {
          const checkDate = addDays(date, i);
          const checkDateString = format(checkDate, "yyyy-MM-dd");
          const checkStockItem = dateToStockItem.get(checkDateString);
          if (!checkStockItem || checkStockItem.allotment === 0 || checkStockItem.is_stopped === 1) {
            return { isAvailable: false, reason: "insufficientConsecutiveNights" };
          }
        }

        return { isAvailable: true };
      } else {
        // Arrival date is selected. We need to check if date is available as a departure date.

        if (isSameDay(date, selectedArrival)) {
          return { isAvailable: true, reason: "sameAsArrival" };
        }

        if (!isAfter(date, selectedArrival)) {
          return { isAvailable: false, reason: "beforeArrival" };
        }

        // Get maximum minimum stay from arrival date and any dates in the range
        const arrivalDateString = format(selectedArrival, "yyyy-MM-dd");
        const arrivalStockItem = dateToStockItem.get(arrivalDateString);
        let minimumStay = arrivalStockItem?.minimum_stay || 1;
        const maxMinStay = arrivalStockItem?.maximum_minimum_stay || 1;

        // If not the same day as arrival + max min stay, then we need to check
        if (!isSameDay(date, addDays(selectedArrival, maxMinStay))) {


        // Check all dates in range for higher minimum stays
        for (let checkDate = selectedArrival; checkDate < date; checkDate = addDays(checkDate, 1)) {
          const checkDateString = format(checkDate, "yyyy-MM-dd");
          const checkStockItem = dateToStockItem.get(checkDateString);
          if (checkStockItem?.minimum_stay && checkStockItem.minimum_stay > minimumStay) {
            minimumStay = checkStockItem.minimum_stay;
          }
        }

        const totalNights = differenceInDays(date, selectedArrival);

        if (totalNights < minimumStay) {
          return { isAvailable: false, reason: "belowMinimumStay" };
          }
        }

        // Check that all nights between selectedArrival and the night before date have availability.
        for (let checkDate = selectedArrival; isBefore(checkDate, date); checkDate = addDays(checkDate, 1)) {
          const checkDateString = format(checkDate, "yyyy-MM-dd");
          const checkStockItem = dateToStockItem.get(checkDateString);
          if (!checkStockItem || checkStockItem.allotment === 0 || checkStockItem.is_stopped === 1) {
            return { isAvailable: false, reason: "unavailableNightInRange" };
          }
        }

        return { isAvailable: true };
      }
    }

    function getMinimumStay(date: Date): number {
      const dateString = format(date, "yyyy-MM-dd");
      const stockItem = dateToStockItem.get(dateString);
      return stockItem ? stockItem.minimum_stay || 1 : 1;
    }

    function getMaximumMinimumStay(date: Date): number {
      const dateString = format(date, "yyyy-MM-dd");
      const stockItem = dateToStockItem.get(dateString);
      return stockItem ? stockItem.maximum_minimum_stay || 1 : 1;
    }

    function isRangeFullyBookable(start: Date, end: Date): boolean {
      // Check that all nights between start (inclusive) and end (exclusive) have availability.
      for (let checkDate = start; isBefore(checkDate, end); checkDate = addDays(checkDate, 1)) {
        const checkDateString = format(checkDate, "yyyy-MM-dd");
        const checkStockItem = dateToStockItem.get(checkDateString);
        if (!checkStockItem || checkStockItem.allotment === 0 || checkStockItem.is_stopped === 1) {
          return false;
        }
      }
      return true;
    }

    return {
      isDateAvailable,
      getMinimumStay,
      getMaximumMinimumStay,
      isRangeFullyBookable,
    };
  }, [availabilityData]);
}

function getCalendarHelpers(currentDate: Date, t: ReturnType<typeof createTranslator>) {
  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: firstDayOfMonth, end: lastDayOfMonth });
  const weekdays = [t("Mon"), t("Tue"), t("Wed"), t("Thu"), t("Fri"), t("Sat"), t("Sun")];

  return { firstDayOfMonth, days, weekdays };
}

function isDateInRange(
  date: Date,
  selectedArrival: Date | null,
  selectedDeparture: Date | null
): boolean {
  if (selectedArrival && selectedDeparture) {
    return isWithinInterval(date, { start: selectedArrival, end: selectedDeparture });
  }
  return false;
}

interface DayCellProps {
  date: Date;
  selectedArrival: Date | null;
  selectedDeparture: Date | null;
  handleDateClick: (date: Date) => void;
  isDateAvailable: (
    date: Date,
    selectedArrival: Date | null
  ) => { isAvailable: boolean; reason?: string };
  getMinimumStay: (date: Date) => number;
  getMaximumMinimumStay: (date: Date) => number;
  isRangeFullyBookable: (start: Date, end: Date) => boolean;
}

function DayCell({
  date,
  selectedArrival,
  selectedDeparture,
  handleDateClick,
  isDateAvailable,
  getMinimumStay,
  getMaximumMinimumStay,
  isRangeFullyBookable,
}: DayCellProps) {
  const DEBUG_FLAG = false;
  const { isAvailable, reason } = isDateAvailable(date, selectedArrival);

  const isArrival = selectedArrival && isSameDay(date, selectedArrival);
  const isDeparture = selectedDeparture && isSameDay(date, selectedDeparture);
  const isInRange = isDateInRange(date, selectedArrival, selectedDeparture);
  const minimumStay = DEBUG_FLAG ? (selectedArrival ? getMinimumStay(date) : getMaximumMinimumStay(date)) : getMaximumMinimumStay(date);
  //const minimumStay = getMaximumMinimumStay(date);
  const isFullyBookable =
    selectedArrival &&
    isInRange &&
    (isSameDay(date, selectedArrival) || isRangeFullyBookable(selectedArrival, date));

  // TODO: remove this because it doesnt do anything
  const isValidDepartureDate = true;

  const cellClasses = cn("text-center text-sm w-full h-full relative");

  const buttonClasses = cn(
    "flex flex-col items-center justify-center font-semibold transition-all rounded-md w-full h-full",
    {
      "bg-lime-100 text-lime-900 hover:bg-lime-200 hover:scale-110": isAvailable && !isInRange && !isArrival && !isDeparture,
      "bg-gray-100 text-gray-400": !isAvailable && !isArrival && !isDeparture,
      "bg-yellow-100 text-yellow-800 border-2 border-dashed border-yellow-400": reason === "insufficientConsecutiveNights" && !isArrival && !isDeparture,
      "bg-blue-600 text-white hover:bg-blue-500 rounded-l-2xl hover:scale-110": isArrival,
      "bg-green-600 text-white hover:bg-green-500 rounded-r-2xl hover:scale-110": isDeparture,
      "bg-blue-100 text-blue-800 hover:bg-blue-200 shadow-inner hover:scale-110":
        isInRange && isFullyBookable && !isArrival && !isDeparture && (isValidDepartureDate) && reason !== "belowMinimumStay",
      "bg-blue-100 text-blue-400 border border-dotted border-blue-200":
        isInRange && isFullyBookable && !isArrival && !isDeparture && !isValidDepartureDate,
      "bg-red-100 text-red-800 hover:bg-red-200 border-2 border-red-300":
        isInRange && !isFullyBookable && !isArrival && !isDeparture,
    }
  );

  const handleClick = () => {
    handleDateClick(date);
  };

  return (
    <div
      className={cellClasses}
      role="presentation"
      data-date={format(date, "yyyy-MM-dd")}
      data-reason={reason}
    >
      {(isDeparture || isArrival) && (
        <button
          className={cn(
            "absolute top-0 right-0 z-10 p-1 text-white rounded-full -mt-1 -mr-1 shadow-lg transform hover:scale-110 transition-transform",
            isArrival ? "bg-blue-600 hover:bg-blue-700" : "bg-green-700 hover:bg-green-800"
          )}
          onClick={(e) => {
            e.stopPropagation();
            handleDateClick(date);
          }}
        >
          <X className="h-3 w-3" />
        </button>
      )}
      <button
        className={buttonClasses}
        role="gridcell"
        tabIndex={-1}
        onClick={handleClick}
      >
        <div
          className={cn("text-sm font-bold", {
            "text-white": isArrival || isDeparture,
          })}
        >
          {format(date, "d")}
        </div>
        {(DEBUG_FLAG || (isAvailable && (!selectedArrival || isArrival))) &&  (
          <div
            className={cn("text-center text-[0.7rem]", {
              "text-lime-700": !isInRange,
              "text-blue-700": isInRange && isFullyBookable,
              "text-red-700": isInRange && !isFullyBookable,
              "text-white": isArrival || isDeparture,
            })}
          >
            {minimumStay < 10 && <span className="text-[0.6rem]">min. </span>}
            {minimumStay} 🌙
          </div>
        )}
      </button>
    </div>
  );
}

interface CalendarGridProps {
  weekdays: string[];
  firstDayOfMonth: Date;
  currentDate: Date;
  selectedArrival: Date | null;
  selectedDeparture: Date | null;
  handleDateClick: (date: Date) => void;
  isDateAvailable: (
    date: Date,
    selectedArrival: Date | null
  ) => { isAvailable: boolean; reason?: string };
  getMinimumStay: (date: Date) => number;
  getMaximumMinimumStay: (date: Date) => number;
  isRangeFullyBookable: (start: Date, end: Date) => boolean;
  formatter: ReturnType<typeof createFormatter>;
}

function CalendarGrid({
  weekdays,
  firstDayOfMonth,
  currentDate,
  selectedArrival,
  selectedDeparture,
  handleDateClick,
  isDateAvailable,
  getMinimumStay,
  getMaximumMinimumStay,
  isRangeFullyBookable,
  formatter,
}: CalendarGridProps) {
  // Calculate the offset for the first day of the month
  // If it's Sunday (0), we want to show it as the 7th day
  const firstDayOffset = firstDayOfMonth.getDay() || 7;

  return (
    <CardContent className="py-4 px-2 inline-block w-full sm:w-auto">
      <h2 className="text-lg font-semibold mb-2 text-center">
        {formatter.dateTime(currentDate, { month: "long", year: "numeric" })}
      </h2>
      <div className="grid gap-1 [grid-template-columns:repeat(7,minmax(50px,1fr))] justify-center ">
        {weekdays.map((day) => (
          <div
            key={day}
            className="text-sm font-medium text-muted-foreground p-0.5 text-center"
          >
            {day}
          </div>
        ))}
        {Array.from({ length: 7 * 6 }).map((_, index) => {
          const date = addDays(firstDayOfMonth, index - firstDayOffset + 1);
          if (isSameMonth(date, currentDate)) {
            return (
              <div key={date.toISOString()} className="aspect-square flex items-center justify-center ">
                <DayCell
                  date={date}
                  selectedArrival={selectedArrival}
                  selectedDeparture={selectedDeparture}
                  handleDateClick={handleDateClick}
                  isDateAvailable={isDateAvailable}
                  getMinimumStay={getMinimumStay}
                  getMaximumMinimumStay={getMaximumMinimumStay}
                  isRangeFullyBookable={isRangeFullyBookable}
                />
              </div>
            );
          }
          return (
            <div key={`empty-${index}`} className="aspect-square flex items-center justify-center " />
          );
        })}
      </div>
    </CardContent>
  );
}

interface AvailabilityCalendarProps {
  availabilityData: StockItem[];
  roomName: string;
  roomDescription: string;
  bookingUrl: string;
  locale: Locale;
  hotelId: string;
  roomId: string;
  onDateChange?: (arrival: Date | null, departure: Date | null) => void;
  initialArrival?: string;
  initialDeparture?: string;
}

interface CalendarError {
  key: string;
  message: string;
}


function AvailabilityCalendar({
  availabilityData,
  roomName,
  roomDescription,
  bookingUrl,
  locale,
  hotelId,
  roomId,
  onDateChange,
  initialArrival,
  initialDeparture,
}: AvailabilityCalendarProps) {
  const t = createTranslator(locale);
  const formatter = createFormatter(locale);
  const totalMonths = 18; // Total number of months to load
  const listRef = useRef<List>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [calendarError, setCalendarError] = useState<CalendarError | null>(null);
  const [lastClickedDate, setLastClickedDate] = useState<string | null>(null);
  const [selectedArrival, setSelectedArrival] = useState<Date | null>(null);
  const [selectedDeparture, setSelectedDeparture] = useState<Date | null>(null);
  const [isInitialPrefillError, setIsInitialPrefillError] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { isDateAvailable, getMinimumStay, getMaximumMinimumStay, isRangeFullyBookable } =
    useAvailabilityData(availabilityData);

  const parseInitialDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getPrefillErrorMessage = (reason?: string, fallbackDate?: Date | null) => {
    if (reason === "pastDate") {
      return t("errors.pastDate");
    }
    if (reason === "insufficientConsecutiveNights") {
      return t("errors.cannotSelectAsArrival");
    }
    if (reason === "beforeArrival") {
      return t("errors.beforeArrival");
    }
    if (reason === "belowMinimumStay" && fallbackDate) {
      return t("errors.belowMinimumStay", { nights: getMinimumStay(fallbackDate) });
    }
    if (reason === "unavailableNightInRange") {
      return t("errors.unavailableNightInRange");
    }
    return t("errors.noAvailability");
  };

  useEffect(() => {
    if (!initialArrival && !initialDeparture) {
      setIsInitialPrefillError(false);
      return;
    }

    setCalendarError(null);
    setIsInitialPrefillError(false);

    const arrivalDate = parseInitialDate(initialArrival);
    const departureDate = parseInitialDate(initialDeparture);

    if (!arrivalDate) {
      return;
    }

    const arrivalAvailability = isDateAvailable(arrivalDate, null);
    if (!arrivalAvailability.isAvailable) {
      setIsInitialPrefillError(true);
      setCalendarError({
        key: arrivalAvailability.reason || "noAvailability",
        message: getPrefillErrorMessage(arrivalAvailability.reason),
      });
      return;
    }

    setSelectedArrival(arrivalDate);

    if (!departureDate) {
      return;
    }

    const departureAvailability = isDateAvailable(departureDate, arrivalDate);
    if (!departureAvailability.isAvailable) {
      setIsInitialPrefillError(true);
      setCalendarError({
        key: departureAvailability.reason || "noAvailability",
        message: getPrefillErrorMessage(departureAvailability.reason, arrivalDate),
      });
      return;
    }

    setSelectedDeparture(departureDate);
  }, [initialArrival, initialDeparture, isDateAvailable, getMinimumStay]);

  // Call onDateChange callback when dates change
  useEffect(() => {
    if (onDateChange) {
      onDateChange(selectedArrival, selectedDeparture);
    }
  }, [selectedArrival, selectedDeparture, onDateChange]);

  // Print selected dates for debugging
  useEffect(() => {
    console.log('Selected Arrival:', selectedArrival ? selectedArrival.toISOString() : 'null');
    console.log('Selected Departure:', selectedDeparture ? selectedDeparture.toISOString() : 'null');
  }, [selectedArrival, selectedDeparture]);

  // Reset error after 5 seconds
  useEffect(() => {
    if (calendarError) {
      if (isInitialPrefillError) return;
      const timer = setTimeout(() => {
        setCalendarError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [calendarError, isInitialPrefillError]);

  const handleDateClickWrapper = (clickedDate: Date) => {
    if (isInitialPrefillError) {
      setIsInitialPrefillError(false);
    }

    // Set last clicked date
    setLastClickedDate(clickedDate.toISOString().split('T')[0]);

    // Clear any existing error
    setCalendarError(null);

    // Check if date is in the past
    if (isBefore(clickedDate, startOfDay(new Date()))) {
      setCalendarError({ key: "pastDate", message: t("errors.pastDate") });
      return;
    }

    // Get availability info
    const availability = isDateAvailable(clickedDate, selectedArrival);

    // Handle date selection based on current state
    if (!selectedArrival) {
      // Selecting arrival date
      if (availability.reason === "insufficientConsecutiveNights") {
        setCalendarError({ key: "cannotSelectAsArrival", message: t("errors.cannotSelectAsArrival") });
        trackClickUnavailableDate(format(clickedDate, 'yyyy-MM-dd'), 'insufficientConsecutiveNights', hotelId, roomId);
        return;
      }

      if (!availability.isAvailable) {
        setCalendarError({ key: "noAvailability", message: t("errors.noAvailability") });
        trackClickUnavailableDate(format(clickedDate, 'yyyy-MM-dd'), 'noAvailability', hotelId, roomId);
        return;
      }

      setSelectedArrival(clickedDate);
      trackSelectArrivalDate(format(clickedDate, 'yyyy-MM-dd'), hotelId, roomId);

    } else if (!selectedDeparture) {
      // Selecting departure date
      if (isSameDay(clickedDate, selectedArrival)) {
        setSelectedArrival(null);
        return;
      }

      if (isBefore(clickedDate, selectedArrival)) {
        setCalendarError({ key: "beforeArrival", message: t("errors.beforeArrival") });
        return;
      }

      const minimumStay = getMinimumStay(selectedArrival);
      const minDepartureDate = addDays(selectedArrival, minimumStay);

      if (isBefore(clickedDate, minDepartureDate)) {
        setCalendarError({ key: "belowMinimumStay", message: t("errors.belowMinimumStay", { nights: minimumStay }) });
        trackMinimumStayError(differenceInDays(clickedDate, selectedArrival), minimumStay, hotelId, roomId);
        return;
      }



      if (!isRangeFullyBookable(selectedArrival, clickedDate)) {
        // Check if range is more than 3 months
        const monthDiff = differenceInMonths(clickedDate, selectedArrival);
        if (monthDiff >= 3) {
          setCalendarError({ key: "dateRangeTooLong", message: t("errors.dateRangeTooLong", { months: monthDiff }) });
          return;
        }

        setCalendarError({ key: "unavailableNightInRange", message: t("errors.unavailableNightInRange") });
        return;
      }

      if (!availability.isAvailable) {
        setCalendarError({ key: "noAvailability", message: t("errors.noAvailability") });
        return;
      }

      setSelectedDeparture(clickedDate);
      const nights = differenceInDays(clickedDate, selectedArrival);
      trackSelectDepartureDate(format(clickedDate, 'yyyy-MM-dd'), format(selectedArrival, 'yyyy-MM-dd'), nights, hotelId, roomId);

    } else {
      // Both dates already selected
      if (isSameDay(clickedDate, selectedArrival) || isSameDay(clickedDate, selectedDeparture)) {
        // Reset selection if clicking on existing dates
        setSelectedArrival(null);
        setSelectedDeparture(null);
        trackClearDateSelection(hotelId, roomId);
        return;
      }

      const minimumStay = getMinimumStay(selectedArrival);
      const minDepartureDate = addDays(selectedArrival, minimumStay);

      if (isBefore(clickedDate, minDepartureDate)) {
        setCalendarError({ key: "belowMinimumStay", message: t("errors.belowMinimumStay", { nights: minimumStay }) });
        trackMinimumStayError(differenceInDays(clickedDate, selectedArrival), minimumStay, hotelId, roomId);
        return;
      }


      // Otherwise update departure date
      if (!availability.isAvailable) {
        // Check if range is more than 3 months
        const monthDiff = differenceInMonths(clickedDate, selectedArrival);
        if (monthDiff >= 3) {
          setCalendarError({ key: "dateRangeTooLong", message: t("errors.dateRangeTooLong", { months: monthDiff }) });
          return;
        }

        setCalendarError({ key: "noAvailability", message: t("errors.noAvailability") });
        return;
      }

      setSelectedDeparture(clickedDate);
      const nights = differenceInDays(clickedDate, selectedArrival);
      trackSelectDepartureDate(format(clickedDate, 'yyyy-MM-dd'), format(selectedArrival, 'yyyy-MM-dd'), nights, hotelId, roomId);
    }
  };

  const months = useMemo(() => {
     // Calculate availability for each month
    const getMonthAvailability = (date: Date) => {
      const _startOfMonth = startOfMonth(date);
      const _endOfMonth = endOfMonth(date);
      let availableDays = 0;
      let totalDays = 0;

      let currentDate = _startOfMonth;
      while (isBefore(currentDate, _endOfMonth) || isSameDay(currentDate, _endOfMonth)) {
        if (isDateAvailable(currentDate, null).isAvailable) {
          availableDays++;
        }
        totalDays++;
        currentDate = addDays(currentDate, 1);
      }

      return (availableDays / totalDays) * 100;
    };

    const startDate = new Date();
    return Array.from({ length: totalMonths }, (_, i) => {
      const date = addMonths(startDate, i);
      return {
        index: i,
        date,
        fullMonth: date.getMonth(),
        fullYear: date.getFullYear(),
        name: formatter.dateTime(date, { month: "short" }),
        availability: getMonthAvailability(date)
      };
    });
  }, [totalMonths, formatter, isDateAvailable]);

  const visibleMonths = useMemo(() => {
    const monthsByYear: Record<number, typeof months> = {};
    months.forEach((month) => {
      const year = month.fullYear;
      if (!monthsByYear[year]) {
        monthsByYear[year] = [];
      }
      monthsByYear[year].push(month);
    });
    return Object.keys(monthsByYear).map((year) => ({
      year: parseInt(year, 10),
      months: monthsByYear[parseInt(year, 10)],
    }));
  }, [months]);

  type MonthItemProps = {
    index: number;
    style: React.CSSProperties;
  };

  const MonthItem: React.FC<MonthItemProps> = ({ index, style }) => {
    const month = months[index];
    const monthDate = month.date;
    const { firstDayOfMonth, weekdays } = getCalendarHelpers(monthDate, t);

    return (
      <div style={style}>
        <CalendarGrid
          weekdays={weekdays}
          firstDayOfMonth={firstDayOfMonth}
          currentDate={monthDate}
          selectedArrival={selectedArrival}
          selectedDeparture={selectedDeparture}
          handleDateClick={handleDateClickWrapper}
          isDateAvailable={isDateAvailable}
          getMinimumStay={getMinimumStay}
          getMaximumMinimumStay={getMaximumMinimumStay}
          isRangeFullyBookable={isRangeFullyBookable}
          formatter={formatter}
        />
      </div>
    );
  };

  const scrollToMonth = (index: number) => {
    if (listRef.current) {
      listRef.current.scrollToItem(index, "start");
      // Track month navigation
      const targetMonth = months[index];
      if (targetMonth) {
        trackNavigateMonth(format(targetMonth.date, 'yyyy-MM'), hotelId, roomId);
      }
    }
  };

  const estimatedPrice = useMemo(() => {
    if (!selectedArrival || !selectedDeparture) return null;
    let totalPrice = 0;
    let currentDate = selectedArrival;
    while (isBefore(currentDate, selectedDeparture)) {
      const dateString = format(currentDate, "yyyy-MM-dd");
      const price = availabilityData.find((item) => item.valid_at === dateString)?.price;
      if (price) {
        // Remove currency symbols and parse the number
        // e.g., "€150.00" -> "150.00" -> 150.00
        const numericPrice = parseFloat(price.toString().replace(/[^0-9.-]/g, ''));
        if (!isNaN(numericPrice)) {
          totalPrice += numericPrice;
        }
      }
      currentDate = addDays(currentDate, 1);
    }
    return totalPrice;
  }, [availabilityData, selectedArrival, selectedDeparture]);

  // Track when price becomes visible
  useEffect(() => {
    if (estimatedPrice !== null && selectedArrival && selectedDeparture) {
      const nights = differenceInDays(selectedDeparture, selectedArrival);
      trackViewPrice(estimatedPrice, 'EUR', nights, hotelId, roomId);
    }
  }, [estimatedPrice, selectedArrival, selectedDeparture, hotelId, roomId]);

  const handleReset = () => {
    setSelectedArrival(null);
    setSelectedDeparture(null);
    setCalendarError(null);
    trackClearDateSelection(hotelId, roomId);
  };

  return (
    <Card className="w-full max-w-full mx-auto">
      <CardHeader>
        <CardTitle>{roomName}</CardTitle>
        <div className="text-sm">
          <div className="text-gray-700" >{roomDescription}</div>
        </div>

        <Separator className="" />
        <div className="flex flex-wrap gap-2 w-full mt-4">
          {visibleMonths.map((yearData) => (
            <React.Fragment key={yearData.year}>
              <span className="font-bold text-sm flex items-center">{yearData.year}</span>
              {yearData.months.map((month) => (
                <Button
                  key={`${month.fullYear}-${month.fullMonth}`}
                  id={`btn-month-${month.fullYear}-${String(month.fullMonth + 1).padStart(2, '0')}`}
                  variant="outline"
                  size="sm"
                  className={cn(
                    "px-2 py-0.5 text-xs relative",
                    currentMonthIndex === month.index && "bg-primary text-white ring-2 ring-primary ring-offset-2",
                    currentMonthIndex !== month.index && "hover:scale-110"
                  )}
                  style={{
                    backgroundColor: `hsl(${month.availability}, 70%, 90%)`,
                    color: `hsl(${month.availability}, 70%, 25%)`,
                    borderColor: `hsl(${month.availability}, 70%, 80%)`
                  }}
                  onClick={() => scrollToMonth(month.index)}
                >
                  {month.name}
                </Button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        <div ref={containerRef}>
          <div>
            <List
              height={450} // Adjust as needed
              width={containerWidth}
              itemCount={months.length}
              itemSize={400} // Fixed width per month
              layout="horizontal"
              ref={listRef}
              onScroll={({ scrollOffset }: ListOnScrollProps) => {
                const monthWidth = 400; // Adjust if necessary
                const newIndex = Math.round(scrollOffset / monthWidth);
                setCurrentMonthIndex(newIndex);
              }}
            >
              {MonthItem}
            </List>
          </div>
        </div>
      </CardContent>

      <CalendarFooter
        selectedArrival={selectedArrival}
        selectedDeparture={selectedDeparture}
        estimatedPrice={estimatedPrice}
        getMinimumStay={getMinimumStay}
        bookingUrl={bookingUrl}
        handleReset={handleReset}
        scrollToMonth={scrollToMonth}
        months={months}
        error={calendarError}
        lastClickedDate={lastClickedDate}
        hotelId={hotelId}
        roomId={roomId}
        t={t}
        formatter={formatter}
      />
    </Card>
  );
}

interface CalendarFooterProps {
  selectedArrival: Date | null;
  selectedDeparture: Date | null;
  estimatedPrice: number | null;
  getMinimumStay: (date: Date) => number;
  bookingUrl: string;
  handleReset: () => void;
  scrollToMonth: (index: number) => void;
  months: { index: number; date: Date }[];
  error: CalendarError | null;
  lastClickedDate: string | null;
  hotelId: string;
  roomId: string;
  t: ReturnType<typeof createTranslator>;
  formatter: ReturnType<typeof createFormatter>;
}

function CalendarFooter({
  selectedArrival,
  selectedDeparture,
  estimatedPrice,
  getMinimumStay,
  bookingUrl,
  handleReset,
  scrollToMonth,
  months,
  error,
  lastClickedDate,
  hotelId,
  roomId,
  t,
  formatter
}: CalendarFooterProps) {
  // The URL may already come with a ?Language=nl-NL, but might also not have it.
  // When adding arrival and departure dates, it should be added to the URL in a resilient way
  const arrivalDate = selectedArrival ? format(selectedArrival, 'yyyy-MM-dd') : '';
  const departureDate = selectedDeparture ? format(selectedDeparture, 'yyyy-MM-dd') : '';
  const modifiedBookingUrl = bookingUrl.includes('?')
    ? `${bookingUrl}&mewsStart=${arrivalDate}&mewsEnd=${departureDate}`
    : `${bookingUrl}?mewsStart=${arrivalDate}&mewsEnd=${departureDate}`;

  const handleDateClick = (date: Date | null) => {
    if (date) {
      const monthIndex = months.findIndex(month =>
        isSameMonth(month.date, date) && month.date.getFullYear() === date.getFullYear()
      );
      if (monthIndex !== -1) {
        scrollToMonth(monthIndex);
      }
    }
  };

  return (
    <CardFooter className="bg-blue-50 border-t border-blue-200 flex justify-center p-4 shadow-inner">
      <div className="w-full max-w-md space-y-3 pt-2">
        {error && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium flex items-center justify-center motion-safe:animate-bounce">
            <AlertCircle className="h-5 w-5 mr-2" />
            <p id={`calendar-error-${error.key}`} data-attribute-date-arrival={arrivalDate!="" ? arrivalDate : 'unselected'} data-attribute-date-departure={departureDate!="" ? departureDate : 'unselected'} data-attribute-date-click={lastClickedDate!="" ? lastClickedDate : 'unselected'}>{error.message}</p>
          </div>
        )}

        <div className={cn(
          "space-y-2 text-sm font-bold text-center flex items-center justify-between p-3 rounded-lg bg-blue-100",
          selectedArrival && selectedDeparture ? "bg-green-100" : "bg-blue-100"
        )}>
          {!selectedArrival && !selectedDeparture && (
            <div className="text-sm text-blue-600 font-medium flex items-center">
              <PlaneLanding className="h-5 w-5 mr-2" />
              {t("selectArrivalDate")}
            </div>
          )}
          {selectedArrival && !selectedDeparture && (
            <div className="text-sm text-blue-600 font-medium flex items-center">
              <PlaneTakeoff className="h-5 w-5 mr-2" />
              {t("selectDepartureDate", { nights: getMinimumStay(selectedArrival) })}
            </div>
          )}
          {selectedArrival && selectedDeparture && (
            <div className="flex flex-col space-y-2 w-full">

              {estimatedPrice !== null && (
                <div className="text-sm text-gray-600 flex items-center justify-center">

                  {t("estimatedPrice", {
                    price: formatter.number(estimatedPrice, {
                      style: 'currency',
                      currency: 'EUR'
                    })
                  })}
                </div>
              )}
              <Button
                id="btn-book-now"
                variant="ghost"
                href={modifiedBookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  const nights = selectedArrival && selectedDeparture ? differenceInDays(selectedDeparture, selectedArrival) : 0;
                  trackBeginCheckout(estimatedPrice || 0, 'EUR', arrivalDate, departureDate, nights, hotelId, roomId);
                }}
                className="w-full font-bold py-2 px-4 rounded text-sm bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                {t("bookNow")}
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-row space-x-2">
          {selectedArrival && (
            <div
              className={cn(
              "flex-1 flex flex-col sm:flex-row justify-center sm:justify-between items-center text-sm p-3 rounded-lg shadow-sm cursor-pointer",
              selectedArrival ? "bg-white" : "bg-gray-100 text-gray-500"
            )}
            onClick={() => handleDateClick(selectedArrival)}
          >
            <span className="font-semibold flex items-center mb-1 sm:mb-0">
              <PlaneLanding className="h-5 w-5 mr-2" />
              {t("arrival")}
            </span>
            <span className="font-medium text-center sm:text-left">
              {arrivalDate
                ? formatter.dateTime(new Date(arrivalDate), { dateStyle: "medium" })
                : t("selectArrivalDate")}
            </span>
          </div>)}

          {selectedDeparture && (
          <div
            className={cn(
              "flex-1 flex flex-col sm:flex-row justify-center sm:justify-between items-center text-sm p-3 rounded-lg shadow-sm cursor-pointer",
              selectedDeparture ? "bg-white" : "bg-gray-100 text-gray-500"
            )}
            onClick={() => handleDateClick(selectedDeparture)}
          >
            <span className="font-semibold flex items-center mb-1 sm:mb-0">
              <PlaneTakeoff className="h-5 w-5 mr-2" />
              {t("departure")}
            </span>
            <span className="font-medium text-center sm:text-left">
              {departureDate
                ? formatter.dateTime(new Date(departureDate), { dateStyle: "medium" })
                : t("selectDepartureDate")}
            </span>
          </div>
          )}
        </div>
        <div className="flex justify-between space-x-2">
          <Button
            id="btn-clear-selection"
            variant="ghost"
            onClick={handleReset}
            className={cn(
              "flex-1 font-bold py-1 sm:py-2 px-2 sm:px-4 rounded text-xs sm:text-sm",
              selectedArrival || selectedDeparture
                ? "bg-red-100 hover:bg-red-200 text-red-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            )}
            disabled={!selectedArrival && !selectedDeparture}
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            {t("resetSelection")}
          </Button>
          <Button
            id="btn-prices"
            variant="ghost"
            href={selectedArrival && selectedDeparture ? modifiedBookingUrl : bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackViewPricesClick(hotelId, roomId);
            }}
            className="flex-1 font-bold py-1 sm:py-2 px-2 sm:px-4 rounded text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Euro className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            {t("viewPrices")}
          </Button>
        </div>
      </div>
    </CardFooter>
  );
}

export { AvailabilityCalendar };
